import { useCallback, useEffect, useMemo, useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { useEncryptedParams } from "../../hooks/useEncryptedParams";
import { encrypt } from "../../utils/encryption";
import Button from "../../components/common/Button";
import SearchableDropdown from "../../components/common/SearchableDropdown";
import DatePicker from "../../components/common/DatePicker";
import reportService from "../../services/reportService";
import customerService from "../../services/customerService";
import alertService from "../../utils/alertService";
import {
  FiUser,
  FiPhone,
  FiFileText,
  FiBox,
  FiCreditCard,
  FiRefreshCw,
  FiDownload,
  FiSearch,
  FiInfo,
  FiChevronDown,
  FiChevronUp,
  FiClock,
} from "react-icons/fi";

function formatCurrency(value) {
  const num = Number(value || 0);
  return `₹${num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatNumber(value) {
  const num = Number(value || 0);
  return new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
  }).format(num);
}

function formatDate(dateString) {
  if (!dateString) return "-";
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return String(dateString);
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return String(dateString);
  }
}

function downloadBlob(blob, fileName) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function formatDateISO(date) {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function getDatesForPreset(preset) {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = now.getMonth();

  switch (preset) {
    case "current_month": {
      const start = new Date(yyyy, mm, 1);
      const end = new Date(yyyy, mm + 1, 0);
      return { startDate: formatDateISO(start), endDate: formatDateISO(end) };
    }
    case "last_2_months": {
      const start = new Date(yyyy, mm - 1, 1);
      const end = new Date(yyyy, mm + 1, 0);
      return { startDate: formatDateISO(start), endDate: formatDateISO(end) };
    }
    case "last_3_months": {
      const start = new Date(yyyy, mm - 2, 1);
      const end = new Date(yyyy, mm + 1, 0);
      return { startDate: formatDateISO(start), endDate: formatDateISO(end) };
    }
    case "quarter": {
      const qStartMonth = Math.floor(mm / 3) * 3;
      const start = new Date(yyyy, qStartMonth, 1);
      const end = new Date(yyyy, qStartMonth + 3, 0);
      return { startDate: formatDateISO(start), endDate: formatDateISO(end) };
    }
    case "half_year": {
      const start = new Date(yyyy, mm - 5, 1);
      const end = new Date(yyyy, mm + 1, 0);
      return { startDate: formatDateISO(start), endDate: formatDateISO(end) };
    }
    case "year": {
      const start = new Date(yyyy, 0, 1);
      const end = new Date(yyyy, 11, 31);
      return { startDate: formatDateISO(start), endDate: formatDateISO(end) };
    }
    default:
      return { startDate: "", endDate: "" };
  }
}

const presetOptions = [
  { value: "current_month", label: "Current Month" },
  { value: "last_2_months", label: "Last 2 Months" },
  { value: "last_3_months", label: "Last 3 Months" },
  { value: "quarter", label: "Quarter" },
  { value: "half_year", label: "Half Year" },
  { value: "year", label: "Year" },
  { value: "custom", label: "Custom Date" },
];

export default function CustomerReportPage() {
  const navigate = useNavigate();
  const { customerId: urlCustomerId } = useEncryptedParams();

  const [activeCustomers, setActiveCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(urlCustomerId || "");
  const [reportData, setReportData] = useState(null);

  const [preset, setPreset] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [isCustomersLoading, setIsCustomersLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [downloadingFormat, setDownloadingFormat] = useState("");
  const [error, setError] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [expandedInvoices, setExpandedInvoices] = useState({});

  const toggleInvoiceExpand = (invoiceId) => {
    setExpandedInvoices((prev) => ({
      ...prev,
      [invoiceId]: !prev[invoiceId],
    }));
  };

  // Sync state if URL param changes
  useEffect(() => {
    if (urlCustomerId) {
      setSelectedCustomerId(String(urlCustomerId));
    }
  }, [urlCustomerId]);

  // Load Active Customers List
  const loadCustomers = useCallback(async () => {
    setIsCustomersLoading(true);
    setError("");
    try {
      const data = await customerService.getActiveCustomers();
      const customerList = Array.isArray(data)
        ? data
        : data?.items || data?.data || [];

      setActiveCustomers(customerList);

      // By default, if no customer is selected in URL or state, select the first customer
      if (customerList.length > 0 && !urlCustomerId && !selectedCustomerId) {
        const firstId = String(
          customerList[0]?.id || customerList[0]?.customerId || customerList[0]?.customer_id || ""
        );
        if (firstId) {
          setSelectedCustomerId(firstId);
        }
      }
    } catch (err) {
      console.error("Error loading active customers:", err);
      setError(err?.message || "Failed to load active customers list.");
    } finally {
      setIsCustomersLoading(false);
    }
  }, [urlCustomerId, selectedCustomerId]);

  // Load Customer Report Data for selected customer
  const fetchCustomerReport = useCallback(
    async (targetCustomerId, start = startDate, end = endDate) => {
      if (!targetCustomerId) {
        setReportData(null);
        return;
      }

      setIsLoading(true);
      setError("");
      try {
        const report = await reportService.getCustomerReport(targetCustomerId, start, end);
        setReportData(report);

        // Auto-expand the first invoice by default
        const invoicesList = report?.invoices || [];
        if (invoicesList.length > 0) {
          const firstInvoiceId = invoicesList[0].sellingMasterId;
          if (firstInvoiceId) {
            setExpandedInvoices({ [firstInvoiceId]: true });
          }
        } else {
          setExpandedInvoices({});
        }
      } catch (err) {
        console.error("Error fetching customer report:", err);
        setReportData(null);
        setError(err?.message || "Failed to fetch customer report data.");
      } finally {
        setIsLoading(false);
      }
    },
    [startDate, endDate]
  );

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  useEffect(() => {
    if (selectedCustomerId) {
      fetchCustomerReport(selectedCustomerId);
    }
  }, [selectedCustomerId, fetchCustomerReport]);

  // Format dropdown options - only show phone, no email
  const customerOptions = useMemo(() => {
    return activeCustomers.map((c) => {
      const id = String(c.id || c.customerId || c.customer_id || "");
      const name = c.name || c.customerName || c.companyName || `Customer #${id}`;
      const phone = c.phone || c.mobile || c.phoneNumber || "";

      return {
        value: id,
        label: phone ? `${name} (${phone})` : name,
      };
    });
  }, [activeCustomers]);

  const handleCustomerChange = (e) => {
    const val = String(e.target.value || "");
    setSelectedCustomerId(val);
    if (val) {
      navigate(`/reports/customer-report/${encrypt(val)}`);
    } else {
      navigate("/reports/customer-report");
    }
  };

  const handleRefresh = () => {
    loadCustomers();
    if (selectedCustomerId) {
      fetchCustomerReport(selectedCustomerId);
    }
  };

  const handlePresetChange = (e) => {
    const selectedPreset = e.target.value;
    setPreset(selectedPreset);

    if (!selectedPreset) {
      setStartDate("");
      setEndDate("");
      return;
    }

    if (selectedPreset === "custom") {
      return;
    }

    const { startDate: start, endDate: end } = getDatesForPreset(selectedPreset);
    setStartDate(start);
    setEndDate(end);
  };

  const handleClearFilters = () => {
    setPreset("");
    setStartDate("");
    setEndDate("");
  };

  const handleDownload = async (format) => {
    if (!selectedCustomerId) {
      alertService.error("Please select a customer first.");
      return;
    }

    setDownloadingFormat(format);
    setError("");
    try {
      const blob = await reportService.downloadCustomerReport(format, selectedCustomerId, startDate, endDate);
      const ext = format === "excel" || format === "xlsx" ? "xlsx" : format;
      const customerName =
        reportData?.customer?.name || reportData?.customer?.customerName || selectedCustomerId;
      const sanitizedName = String(customerName).replace(/[^a-zA-Z0-9_-]/g, "_");

      downloadBlob(blob, `customer-report-${sanitizedName}.${ext}`);
      alertService.success(
        `Customer Report downloaded successfully (${format.toUpperCase()})`
      );
    } catch (err) {
      console.error(`Error downloading ${format} report:`, err);
      alertService.error(
        err?.message || `Failed to download ${format.toUpperCase()} report.`
      );
    } finally {
      setDownloadingFormat("");
    }
  };

  // Compute Summary Statistics
  const customerInfo = useMemo(() => reportData?.customer || {}, [reportData]);
  const invoices = useMemo(() => reportData?.invoices || [], [reportData]);
  const materials = useMemo(() => reportData?.materials || [], [reportData]);
  const payments = useMemo(() => reportData?.payments || [], [reportData]);

  const totals = useMemo(() => {
    const totalInvoiceAmt = invoices.reduce(
      (sum, inv) => sum + Number(inv.grandTotal || inv.totalAmount || inv.amount || 0),
      0
    );
    const totalPaidInvoiceAmt = invoices.reduce(
      (sum, inv) => sum + Number(inv.totalPaid || inv.paidAmount || 0),
      0
    );
    const totalMaterialQty = materials.reduce(
      (sum, mat) => sum + Number(mat.quantity || mat.qty || 0),
      0
    );
    const totalMaterialAmt = materials.reduce(
      (sum, mat) => sum + Number(mat.materialGrandTotal || mat.totalAmount || mat.total || mat.amount || 0),
      0
    );
    const totalPaymentAmt = payments.reduce(
      (sum, pmt) => sum + Number(pmt.paymentAmount || pmt.amount || 0),
      0
    );

    return {
      invoiceCount: invoices.length,
      totalInvoiceAmt,
      totalPaidInvoiceAmt,
      totalRemainingAmt: totalInvoiceAmt - totalPaidInvoiceAmt,
      materialCount: materials.length,
      totalMaterialQty,
      totalMaterialAmt,
      paymentCount: payments.length,
      totalPaymentAmt,
    };
  }, [invoices, materials, payments]);

  // Filtered Arrays based on search term
  const filteredInvoices = useMemo(() => {
    if (!searchTerm.trim()) return invoices;
    const term = searchTerm.toLowerCase();
    return invoices.filter(
      (inv) =>
        String(inv.invoiceNo || inv.invoiceNumber || "").toLowerCase().includes(term) ||
        String(inv.status || "").toLowerCase().includes(term) ||
        String(inv.date || "").toLowerCase().includes(term)
    );
  }, [invoices, searchTerm]);



  return (
    <div className="space-y-5 pb-8 animate-in fade-in duration-500">
      {/* Top Header & Customer Selection Bar */}
      <div className="flex flex-col gap-4 border-b border-slate-200 pb-4 dark:border-slate-800 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <span className="p-1.5 bg-primary-50 text-primary-600 rounded-lg dark:bg-primary-900/20 dark:text-primary-400">
              <FiUser className="w-5 h-5" />
            </span>
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-primary-600">
                Customer Analytics &amp; Reports
              </p>
              <h1 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tight leading-none mt-0.5">
                Customer <span className="text-primary-600">Report</span>
              </h1>
            </div>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 pl-8">
            Complete statements including customer summary, invoices, materials supplied, and payment history.
          </p>
        </div>

        {/* Action Controls & Customer Picker */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3 w-full lg:w-auto">
          <div className="w-full sm:min-w-[280px]">
            <SearchableDropdown
              label="Customer"
              name="customerId"
              value={selectedCustomerId}
              onChange={handleCustomerChange}
              options={customerOptions}
              placeholder={
                isCustomersLoading ? "Loading active customers..." : "Choose a Customer"
              }
              disabled={isCustomersLoading}
            />
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-4 sm:flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleRefresh}
              isLoading={isLoading || isCustomersLoading}
              className="h-10 px-3 col-span-1"
              title="Refresh Customer Data"
            >
              <FiRefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline ml-1.5">Refresh</span>
            </Button>

            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleDownload("pdf")}
              isLoading={downloadingFormat === "pdf"}
              disabled={Boolean(downloadingFormat) || !selectedCustomerId}
              className="h-10 col-span-1"
              title="Download PDF Report"
            >
              <span className="flex items-center gap-1.5 text-rose-600 dark:text-rose-400 font-bold">
                <i className="material-icons text-base">picture_as_pdf</i>
                <span className="text-[10px] sm:text-xs">PDF</span>
              </span>
            </Button>

            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleDownload("xlsx")}
              isLoading={downloadingFormat === "xlsx"}
              disabled={Boolean(downloadingFormat) || !selectedCustomerId}
              className="h-10 col-span-1"
              title="Download Excel Report"
            >
              <span className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400 font-bold">
                <i className="material-icons text-base">grid_on</i>
                <span className="text-[10px] sm:text-xs">EXCEL</span>
              </span>
            </Button>

            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleDownload("csv")}
              isLoading={downloadingFormat === "csv"}
              disabled={Boolean(downloadingFormat) || !selectedCustomerId}
              className="h-10 col-span-1"
              title="Download CSV Report"
            >
              <span className="flex items-center gap-1.5 text-blue-600 dark:text-blue-400 font-bold">
                <FiDownload className="w-3.5 h-3.5" />
                <span className="text-[10px] sm:text-xs">CSV</span>
              </span>
            </Button>
          </div>
        </div>
      </div>

      {/* Date Range Filter Bar */}
      <div
        className={`grid grid-cols-1 gap-3 rounded-xl border border-slate-200 bg-white p-3 sm:p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 transition-all ${
          preset === "custom"
            ? "sm:grid-cols-2 lg:grid-cols-4"
            : "sm:grid-cols-2 lg:grid-cols-3"
        }`}
      >
        <SearchableDropdown
          label="Period / Preset Filter"
          name="preset"
          value={preset}
          onChange={handlePresetChange}
          options={presetOptions}
          placeholder="All Time"
        />

        {preset === "custom" && (
          <>
            <DatePicker
              label="From Date"
              value={startDate}
              onChange={setStartDate}
              placeholder="Select start date"
            />
            <DatePicker
              label="To Date"
              value={endDate}
              onChange={setEndDate}
              placeholder="Select end date"
            />
          </>
        )}

        <div className="flex items-end">
          {(startDate || endDate || preset) && (
            <Button
              variant="secondary"
              size="sm"
              onClick={handleClearFilters}
              className="h-10 w-max px-4 text-xs font-semibold text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-950/20 border-rose-200 dark:border-rose-900/50"
            >
              Clear Filter
            </Button>
          )}
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-xs font-semibold text-rose-700 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-300 flex items-center gap-3 shadow-sm">
          <i className="material-icons text-lg text-rose-500">error_outline</i>
          <span>{error}</span>
        </div>
      )}

      {/* Customer Info Card & Key Stats Banner */}
      {selectedCustomerId && customerInfo ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3.5">
          {/* Card 1: Customer Profile */}
          <div className="relative overflow-hidden bg-gradient-to-br from-slate-50 to-primary-50/30 dark:from-slate-900 dark:to-primary-950/20 p-3.5 rounded-2xl border border-primary-100 dark:border-primary-900/30 shadow-sm flex flex-col justify-between hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
            <div className="absolute top-0 right-0 w-32 h-32 bg-primary-500/5 rounded-bl-full pointer-events-none" />
            <div className="absolute -bottom-4 -left-4 w-20 h-20 bg-primary-500/5 rounded-full pointer-events-none" />
            <div>
              <div className="flex items-center justify-between mb-2 border-b border-primary-100 dark:border-primary-900/30 pb-1.5">
                <span className="text-[10px] font-black uppercase tracking-wider text-primary-600 dark:text-primary-400 flex items-center gap-1.5">
                  <FiInfo className="w-3.5 h-3.5" />
                  Customer Profile
                </span>
                <span className="text-[9px] bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 px-2 py-0.5 rounded-full font-black uppercase tracking-widest">Active</span>
              </div>

              <h2 className="text-base font-black text-slate-900 dark:text-white truncate">
                {customerInfo.name || customerInfo.customerName || customerInfo.companyName || "Selected Customer"}
              </h2>

              <div className="mt-2 space-y-1.5 text-xs text-slate-600 dark:text-slate-300">
                {customerInfo.phone && (
                  <div className="flex items-center gap-2 bg-white/70 dark:bg-slate-800/50 rounded-lg px-2.5 py-1">
                    <FiPhone className="w-3.5 h-3.5 text-primary-500 shrink-0" />
                    <span className="font-medium">{customerInfo.phone}</span>
                  </div>
                )}
                {customerInfo.companyName && customerInfo.companyName !== customerInfo.name && (
                  <div className="flex items-center gap-2 bg-white/70 dark:bg-slate-800/50 rounded-lg px-2.5 py-1">
                    <FiUser className="w-3.5 h-3.5 text-primary-500 shrink-0" />
                    <span className="font-medium text-slate-700 dark:text-slate-200 truncate">
                      {customerInfo.companyName}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-2.5 pt-2 border-t border-primary-100/50 dark:border-primary-900/20 text-[9px] text-slate-400 flex items-center justify-between">
              <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block animate-pulse"></span> Live Report</span>
              <span className="font-mono">{new Date().toLocaleTimeString()}</span>
            </div>
          </div>

          {/* Card 2: Materials Supplied */}
          <div className="relative overflow-hidden bg-white dark:bg-slate-900 p-3.5 rounded-2xl border border-amber-100 dark:border-amber-900/30 shadow-sm flex flex-col justify-between hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
            <div className="absolute top-0 right-0 w-16 h-16 bg-amber-50/80 dark:bg-amber-950/20 rounded-bl-full pointer-events-none" />
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] font-black uppercase tracking-wider text-amber-500/80">
                Materials Supplied
              </span>
              <span className="p-1.5 rounded-lg bg-amber-50 text-amber-600 dark:bg-amber-950/40 dark:text-amber-400 shadow-sm">
                <FiBox className="w-3.5 h-3.5" />
              </span>
            </div>
            <div>
              <p className="text-lg font-black text-amber-600 dark:text-amber-400 font-mono leading-none">
                {formatNumber(totals.totalMaterialQty)}{" "}
                <span className="text-xs font-normal text-slate-400">KG</span>
              </p>
              <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-1 flex items-center justify-between">
                <span>Value</span>
                <span className="font-bold text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 px-1 py-0.5 rounded">
                  {formatCurrency(totals.totalMaterialAmt)}
                </span>
              </p>
            </div>
          </div>

          {/* Card 3: Invoices Total */}
          <div className="relative overflow-hidden bg-white dark:bg-slate-900 p-3.5 rounded-2xl border border-blue-100 dark:border-blue-900/30 shadow-sm flex flex-col justify-between hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
            <div className="absolute top-0 right-0 w-16 h-16 bg-blue-50/80 dark:bg-blue-950/20 rounded-bl-full pointer-events-none" />
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] font-black uppercase tracking-wider text-blue-500/80">
                Invoices Total
              </span>
              <span className="p-1.5 rounded-lg bg-blue-50 text-blue-600 dark:bg-blue-950/40 dark:text-blue-400 shadow-sm">
                <FiFileText className="w-3.5 h-3.5" />
              </span>
            </div>
            <div>
              <p className="text-lg font-black text-blue-700 dark:text-blue-300 font-mono leading-none">
                {formatCurrency(totals.totalInvoiceAmt)}
              </p>
              <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-1 flex items-center justify-between">
                <span>Total Issued</span>
                <span className="font-bold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/30 px-1 py-0.5 rounded">
                  {totals.invoiceCount} {totals.invoiceCount === 1 ? "Invoice" : "Invoices"}
                </span>
              </p>
            </div>
          </div>

          {/* Card 4: Payments Received */}
          <div className="relative overflow-hidden bg-white dark:bg-slate-900 p-3.5 rounded-2xl border border-emerald-100 dark:border-emerald-900/30 shadow-sm flex flex-col justify-between hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
            <div className="absolute top-0 right-0 w-16 h-16 bg-emerald-50/80 dark:bg-emerald-950/20 rounded-bl-full pointer-events-none" />
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] font-black uppercase tracking-wider text-emerald-500/80">
                Payments Received
              </span>
              <span className="p-1.5 rounded-lg bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400 shadow-sm">
                <FiCreditCard className="w-3.5 h-3.5" />
              </span>
            </div>
            <div>
              <p className="text-lg font-black text-emerald-600 dark:text-emerald-400 font-mono leading-none">
                {formatCurrency(totals.totalPaymentAmt)}
              </p>
              <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-1 flex items-center justify-between">
                <span>Transactions</span>
                <span className="font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/30 px-1 py-0.5 rounded">
                  {totals.paymentCount} {totals.paymentCount === 1 ? "Payment" : "Payments"}
                </span>
              </p>
            </div>
          </div>

          {/* Card 5: Remaining Payment */}
          <div className="relative overflow-hidden bg-white dark:bg-slate-900 p-3.5 rounded-2xl border border-rose-100 dark:border-rose-900/30 shadow-sm flex flex-col justify-between hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
            <div className="absolute top-0 right-0 w-16 h-16 bg-rose-50/80 dark:bg-rose-950/20 rounded-bl-full pointer-events-none" />
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] font-black uppercase tracking-wider text-rose-500/80">
                Remaining Payment
              </span>
              <span className="p-1.5 rounded-lg bg-rose-50 text-rose-600 dark:bg-rose-950/40 dark:text-rose-400 shadow-sm">
                <FiClock className="w-3.5 h-3.5" />
              </span>
            </div>
            <div>
              <p className="text-lg font-black text-rose-600 dark:text-rose-400 font-mono leading-none">
                {formatCurrency(totals.totalRemainingAmt)}
              </p>
              <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-1 flex items-center justify-between">
                <span>Balance Due</span>
                <span className={`font-bold px-1 py-0.5 rounded text-[10px] ${
                  totals.totalRemainingAmt > 0
                    ? "text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-950/30 animate-pulse"
                    : "text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/30"
                }`}>
                  {totals.totalRemainingAmt > 0 ? "Pending" : "Settled"}
                </span>
              </p>
            </div>
          </div>
        </div>
      ) : null}

      {/* Main Content Data Tables */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
        {/* Search */}
        <div className="p-3 sm:p-4 border-b border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 bg-gradient-to-r from-slate-50 to-slate-100/50 dark:from-slate-900 dark:to-slate-800/30">
          <div>
            <h2 className="text-sm font-black text-slate-900 dark:text-white tracking-tight">
              Customer Statement Details
            </h2>
            <p className="mt-0.5 text-[11px] font-medium text-slate-500 dark:text-slate-400">
              Invoices, materials, and payments are shown together.
            </p>
          </div>

          {/* Quick Search */}
          <div className="relative min-w-[220px] sm:w-64">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-primary-400 w-3.5 h-3.5" />
            <input
              type="text"
              placeholder="Search in report data..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-white dark:bg-slate-900 border border-primary-200 dark:border-slate-700 text-slate-800 dark:text-slate-200 text-xs rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-400 outline-none transition-all shadow-sm"
            />
          </div>
        </div>

        {/* Content Body */}
        <div className="p-3 sm:p-5 space-y-6">
          {isLoading ? (
            <div className="py-16 text-center text-slate-400 dark:text-slate-500">
              <FiRefreshCw className="animate-spin w-6 h-6 mx-auto mb-2 text-primary-600" />
              <p className="text-xs font-semibold">Loading customer report data...</p>
            </div>
          ) : !selectedCustomerId ? (
            <div className="py-16 text-center text-slate-400 dark:text-slate-500">
              <FiUser className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p className="text-sm font-bold text-slate-700 dark:text-slate-300">
                No Customer Selected
              </p>
              <p className="text-xs mt-1">Please select a customer from the dropdown above to view details.</p>
            </div>
          ) : (
            <>
              {/* SECTION 1: INVOICES TABLE */}
              <div className="space-y-2">
                <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-2">
                  <h3 className="text-xs font-black uppercase tracking-wider text-slate-800 dark:text-slate-200 flex items-center gap-2">
                    <FiFileText className="text-blue-600" />
                    Invoices Statement ({filteredInvoices.length})
                  </h3>
                  <span className="text-[11px] font-bold text-slate-600 dark:text-slate-400">
                    Total: {formatCurrency(totals.totalInvoiceAmt)}
                  </span>
                </div>

                <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 uppercase text-[10px] font-bold">
                      <tr>
                        <th className="py-2.5 px-2 text-center w-10" title="Click chevron to view details">
                          <span className="text-[9px] text-primary-600 dark:text-primary-400 font-black animate-pulse uppercase tracking-wider">Details</span>
                        </th>
                        <th className="py-2.5 px-3">#</th>
                        <th className="py-2.5 px-3">Invoice No</th>
                        <th className="py-2.5 px-3">Invoice Date</th>
                        <th className="py-2.5 px-3">Due Date</th>
                        <th className="py-2.5 px-3 text-right">Total Amount</th>
                        <th className="py-2.5 px-3 text-right">Paid Amount</th>
                        <th className="py-2.5 px-3 text-right">Remaining Amount</th>
                        <th className="py-2.5 px-3 text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-slate-700 dark:text-slate-300">
                      {filteredInvoices.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="py-6 text-center text-slate-400 text-xs">
                            No invoice records found.
                          </td>
                        </tr>
                      ) : (
                        filteredInvoices.map((inv, idx) => {
                          const total = Number(inv.grandTotal || inv.totalAmount || inv.amount || 0);
                          const paid = Number(inv.totalPaid || inv.paidAmount || 0);
                          const balance = total - paid;
                          const status = inv.status || (balance <= 0 ? "PAID" : paid > 0 ? "PARTIAL" : "UNPAID");
                          const isExpanded = !!expandedInvoices[inv.sellingMasterId];

                          return (
                            <Fragment key={inv.sellingMasterId || idx}>
                              <tr className={`transition-colors duration-150 ${isExpanded
                                ? "bg-primary-50/30 dark:bg-primary-950/20 border-l-4 border-primary-500 font-semibold"
                                : "hover:bg-slate-50/80 dark:hover:bg-slate-800/30"
                                }`}>
                                <td className="py-2 px-2 text-center">
                                  <button
                                    onClick={() => toggleInvoiceExpand(inv.sellingMasterId)}
                                    className={`flex items-center justify-center w-7 h-7 rounded-full border transition-all duration-200 cursor-pointer shadow-sm hover:shadow ${isExpanded
                                      ? "bg-primary-50 border-primary-200 text-primary-600 dark:bg-primary-950/40 dark:border-primary-900/50 hover:bg-primary-500 hover:border-primary-500 hover:text-white"
                                      : "bg-primary-50/70 border-primary-200 text-primary-600 dark:bg-primary-950/20 dark:border-primary-900/30 hover:bg-primary-500 hover:border-primary-500 hover:text-white transition-all shadow-sm hover:scale-105"
                                      }`}
                                    title={isExpanded ? "Collapse Row" : "Expand Row"}
                                  >
                                    {isExpanded ? (
                                      <FiChevronUp className="w-4 h-4" />
                                    ) : (
                                      <FiChevronDown className="w-4 h-4" />
                                    )}
                                  </button>
                                </td>
                                <td className="py-2 px-3 text-slate-400 text-[10px] font-mono">{idx + 1}</td>
                                <td className="py-2 px-3 font-bold text-slate-900 dark:text-white">
                                  {inv.invoiceNo || inv.invoiceNumber || inv.no || "-"}
                                </td>
                                <td className="py-2 px-3">{formatDate(inv.date || inv.invoiceDate)}</td>
                                <td className="py-2 px-3">{formatDate(inv.dueDate)}</td>
                                <td className="py-2 px-3 text-right font-semibold">{formatCurrency(total)}</td>
                                <td className="py-2 px-3 text-right font-medium text-emerald-600 dark:text-emerald-400">{formatCurrency(paid)}</td>
                                <td className="py-2 px-3 text-right font-bold text-slate-900 dark:text-white">{formatCurrency(balance)}</td>
                                <td className="py-2 px-3 text-center">
                                  <span
                                    className={`inline-block px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${status.toUpperCase() === "PAID"
                                      ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"
                                      : status.toUpperCase() === "PARTIAL"
                                        ? "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300"
                                        : "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300"
                                      }`}
                                  >
                                    {status}
                                  </span>
                                </td>
                              </tr>
                              {isExpanded && (
                                <tr className="bg-primary-50/15 dark:bg-primary-950/10 transition-all duration-300">
                                  <td colSpan={9} className="p-4 border-b-2 border-primary-200/80 dark:border-primary-900/50">
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 animate-in slide-in-from-top-2 duration-200 text-left">

                                      {/* Materials Table for this particular invoice */}
                                      <div className="bg-white dark:bg-slate-900/80 p-4 rounded-2xl border border-amber-200/80 dark:border-amber-900/30 shadow-md">
                                        <div className="flex items-center justify-between border-b border-amber-100 dark:border-amber-900/20 pb-2 mb-3">
                                          <h4 className="text-xs font-black uppercase tracking-wider text-slate-800 dark:text-slate-200 flex items-center gap-2">
                                            <FiBox className="text-amber-500 w-4 h-4" />
                                            Invoice Materials ({inv.sellingMaterials?.length || 0})
                                          </h4>
                                          <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 px-1.5 py-0.5 rounded-md">
                                            Total Qty: {formatNumber(inv.sellingMaterials?.reduce((sum, m) => sum + Number(m.quantity || 0), 0))} KG
                                          </span>
                                        </div>

                                        <div className="overflow-x-auto rounded-xl border border-slate-100 dark:border-slate-800/60">
                                          <table className="w-full text-left text-[11px]">
                                            <thead className="bg-slate-50 dark:bg-slate-800 text-slate-500 uppercase text-[9px] font-bold">
                                              <tr>
                                                <th className="py-2 px-2.5">Material</th>
                                                <th className="py-2 px-2.5">Description</th>
                                                <th className="py-2 px-2.5">Yard / Lot</th>
                                                <th className="py-2 px-2.5 text-right">Qty</th>
                                                <th className="py-2 px-2.5 text-right">Rate</th>
                                                <th className="py-2 px-2.5 text-right">Total</th>
                                              </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-slate-700 dark:text-slate-300">
                                              {!inv.sellingMaterials || inv.sellingMaterials.length === 0 ? (
                                                <tr>
                                                  <td colSpan={6} className="py-4 text-center text-slate-400">
                                                    No materials sold under this invoice.
                                                  </td>
                                                </tr>
                                              ) : (
                                                inv.sellingMaterials.map((mat, mIdx) => {
                                                  const mQty = Number(mat.quantity || 0);
                                                  const mRate = Number(mat.ratePerKg || mat.rate || 0);
                                                  const mTotal = Number(mat.materialGrandTotal || mat.grandTotal || mat.amount || mQty * mRate);
                                                  return (
                                                    <tr key={mat.sellingMaterialsId || mIdx} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/20 transition-colors">
                                                      <td className="py-2 px-2.5 font-bold text-slate-900 dark:text-white">
                                                        {mat.stockCategoryName || mat.stockCategory || mat.material || "-"}
                                                      </td>
                                                      <td className="py-2 px-2.5 text-slate-500 dark:text-slate-400 italic max-w-[120px] truncate" title={mat.description}>
                                                        {mat.description || "-"}
                                                      </td>
                                                      <td className="py-2 px-2.5 font-medium text-slate-600 dark:text-slate-400">
                                                        {mat.yardNo || mat.yard || "-"} / {mat.lotNo || mat.lot || "-"}
                                                      </td>
                                                      <td className="py-2 px-2.5 text-right font-bold text-slate-800 dark:text-slate-200">
                                                        {formatNumber(mQty)} KG
                                                      </td>
                                                      <td className="py-2 px-2.5 text-right font-medium">{formatCurrency(mRate)}</td>
                                                      <td className="py-2 px-2.5 text-right font-black text-amber-600 dark:text-amber-400 font-mono">
                                                        {formatCurrency(mTotal)}
                                                      </td>
                                                    </tr>
                                                  );
                                                })
                                              )}
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>

                                      {/* Payments Table for this particular invoice */}
                                      <div className="bg-white dark:bg-slate-900/80 p-4 rounded-2xl border border-emerald-200/80 dark:border-emerald-900/30 shadow-md">
                                        <div className="flex items-center justify-between border-b border-emerald-100 dark:border-emerald-900/20 pb-2 mb-3">
                                          <h4 className="text-xs font-black uppercase tracking-wider text-slate-800 dark:text-slate-200 flex items-center gap-2">
                                            <FiCreditCard className="text-emerald-500 w-4 h-4" />
                                            Invoice Payments ({inv.payments?.length || 0})
                                          </h4>
                                          <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/30 px-1.5 py-0.5 rounded-md">
                                            Paid: {formatCurrency(inv.payments?.reduce((sum, p) => sum + Number(p.paymentAmount || 0), 0))}
                                          </span>
                                        </div>

                                        <div className="overflow-x-auto rounded-xl border border-slate-100 dark:border-slate-800/60">
                                          <table className="w-full text-left text-[11px]">
                                            <thead className="bg-slate-50 dark:bg-slate-800 text-slate-500 uppercase text-[9px] font-bold">
                                              <tr>
                                                <th className="py-2 px-2.5">Payment Date</th>
                                                <th className="py-2 px-2.5">Mode</th>
                                                <th className="py-2 px-2.5">Reference No</th>
                                                <th className="py-2 px-2.5">Remarks</th>
                                                <th className="py-2 px-2.5 text-right">Amount Received</th>
                                              </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-slate-700 dark:text-slate-300">
                                              {!inv.payments || inv.payments.length === 0 ? (
                                                <tr>
                                                  <td colSpan={5} className="py-4 text-center text-slate-400">
                                                    No payment transactions received for this invoice.
                                                  </td>
                                                </tr>
                                              ) : (
                                                inv.payments.map((pmt, pIdx) => {
                                                  const pAmt = Number(pmt.paymentAmount || pmt.amount || 0);
                                                  const pDate = pmt.paymentDate || pmt.date;
                                                  const pRef = pmt.transactionRefNo || pmt.referenceNo || pmt.chequeNo || "-";
                                                  return (
                                                    <tr key={pmt.paymentId || pIdx} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/20 transition-colors">
                                                      <td className="py-2 px-2.5 font-medium">{formatDate(pDate)}</td>
                                                      <td className="py-2 px-2.5">
                                                        <span className="inline-flex items-center rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-[9px] font-bold text-slate-700 dark:text-slate-300">
                                                          {pmt.paymentMode || "Bank / Cash"}
                                                        </span>
                                                      </td>
                                                      <td className="py-2 px-2.5 font-mono text-[10px] max-w-[100px] truncate" title={pRef}>
                                                        {pRef}
                                                      </td>
                                                      <td className="py-2 px-2.5 text-slate-500 dark:text-slate-400 italic max-w-[120px] truncate" title={pmt.remarks || pmt.notes}>
                                                        {pmt.remarks || pmt.notes || "-"}
                                                      </td>
                                                      <td className="py-2 px-2.5 text-right font-black text-emerald-600 dark:text-emerald-400 font-mono">
                                                        {formatCurrency(pAmt)}
                                                      </td>
                                                    </tr>
                                                  );
                                                })
                                              )}
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>

                                    </div>
                                  </td>
                                </tr>
                              )}
                            </Fragment>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
